/**
 * A wrapper component for a grid layout.
 *
 * @param {Object} props - The properties object.
 * @param {ReactNode} props.children - The child components to be rendered within the grid.
 * @param {number} [props.columns=3] - The number of columns in the grid, used if responsive is empty.
 * @param {number} [props.gap=4] - The gap between grid items.
 */
const GridWrapper = ({
  children,
  columns = 2, // Only used if responsive is empty
  gap = 4,
  className = "",
  responsive = {
    sm: 1,
    md: 2,
    lg: 3,
  },
  ...props
}) => {
  const columnClasses = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
  };

  const gapClasses = {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    6: "gap-6",
    8: "gap-8",
  };

  const responsiveClassesMap = {
    sm: {
      1: "sm:grid-cols-1",
      2: "sm:grid-cols-2",
      3: "sm:grid-cols-3",
      4: "sm:grid-cols-4",
    },
    md: {
      1: "md:grid-cols-1",
      2: "md:grid-cols-2",
      3: "md:grid-cols-3",
      4: "md:grid-cols-4",
    },
    lg: {
      1: "lg:grid-cols-1",
      2: "lg:grid-cols-2",
      3: "lg:grid-cols-3",
      4: "lg:grid-cols-4",
      5: "lg:grid-cols-5",
      6: "lg:grid-cols-6",
    },
  };

  /**
   * Generates a string of CSS classes for a grid layout based on specified settings.
   *
   * @param {Object} responsive - An object defining the number of columns at different breakpoints.
   * @param {number} columns - The default number of columns when no responsive setting is provided.
   * @param {string} className - Additional custom classes to append to the grid classes.
   * @param {number} gap - The gap size between grid items.
   * @returns {string} A string of space-separated CSS classes for the grid layout.
   */
  const getGridClasses = () => {
    const baseClasses = "grid";
    const gapClass = gapClasses[gap] || gapClasses[4]; // Default to gap-4 if not found

    // Check if responsive is empty and set default grid class based on columns
    const hasResponsive = Object.keys(responsive).length > 0;
    const defaultGridClass =
      !hasResponsive && columnClasses[columns] ? columnClasses[columns] : "";

    // Generate responsive classes based on the provided responsive object
    // If responsive is empty, default to the columns value
    const responsiveClasses = hasResponsive
      ? [
          "grid-cols-1", // Default to 1 column for the smallest size
          ...Object.entries(responsive)
            .map(
              ([breakpoint, cols]) =>
                responsiveClassesMap[breakpoint]?.[cols] || "",
            )
            .filter(Boolean),
        ].join(" ")
      : "";

    const finalClasses =
      `${baseClasses} ${gapClass} ${defaultGridClass} ${responsiveClasses} ${className}`.trim();
    return finalClasses;
  };

  return (
    <div className={getGridClasses()} {...props}>
      {children}
    </div>
  );
};

export default GridWrapper;
